/**
 * Stale chunk after a deploy (G2-2). A tab opened before a push to main still
 * holds the previous build's index.html; its next lazy route asks for a hashed
 * chunk that is no longer on the server (index.js answers with 404) and the
 * dynamic import rejects. Every dynamic import in the bundle goes through
 * Vite's preload helper, which dispatches `vite:preloadError` and then throws,
 * so the rejection also reaches the root ErrorBoundary via React.lazy. Both
 * sites call in here: reload the tab once so it picks up the current build.
 *
 * Loop guard: sessionStorage remembers which build the reload left from -- the
 * entry chunk's URL, whose hash changes with every deploy. When a chunk fails
 * again on that same build it is broken rather than stale, the caller falls
 * through to its error screen and no second reload happens.
 */
const STORAGE_KEY = 'stale-chunk-reload';
const CHUNK_LOAD_ERROR =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

let reloading = false;

const currentBuild = () =>
  document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? '';

export const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);

/** True once a reload has been triggered in this document: nothing is worth painting until it lands. */
export const isReloadingForStaleChunk = () => reloading;

/**
 * Reloads the tab for a chunk-load error, once per build. Returns true when the
 * page is going away (now or already), false when the caller should render its
 * own error state.
 */
export function reloadForStaleChunk(error: unknown): boolean {
  if (reloading) {
    return true;
  }
  if (!isChunkLoadError(error)) {
    return false;
  }
  const build = currentBuild();
  if (sessionStorage.getItem(STORAGE_KEY) === build) {
    return false;
  }
  sessionStorage.setItem(STORAGE_KEY, build);
  reloading = true;
  window.location.reload();
  return true;
}
